/**
 * Auth context. Exposes the current user, a token getter, and sign-in /
 * sign-out to the WS singleton and the API client.
 *
 * Which sign-in runs is the server's call: the provider reads
 * ``GET /api/v1/auth/config`` and builds the matching backend
 * (``backends.ts``) — local accounts, or Neon Auth with self sign-up.
 */
import * as React from "react";
import { AuthContext } from "./context";
import { type AuthBackend, buildAuthBackend, fetchAuthConfig } from "./backends";
import { decodeClaims, isExpired } from "./token";

export interface AuthUser {
  uid: string;
  email: string | null;
  /** Local part of the email — the backend stores no display name. */
  displayName: string;
}

export interface AuthState {
  user: AuthUser | null;
  ready: boolean;
  /**
   * True when the token's ``role`` claim is ``admin``. Mirrors the
   * backend's ``_require_admin`` so admin-only UI can hide itself
   * without probing and collecting 403s. ``null`` until known.
   */
  isAdmin: boolean | null;
  /** Set when the sign-in settings could not be loaded or are unsupported. */
  error?: string | null;
  /** True when this server lets people create their own accounts. */
  canSignUp?: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp?: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** A valid token, or null when signed out or expired. */
  getIdToken: () => Promise<string | null>;
}

type Session = { user: AuthUser; isAdmin: boolean };

function sessionFromToken(token: string): Session | null {
  const claims = decodeClaims(token);
  if (!claims || isExpired(token)) return null;
  return {
    user: {
      uid: claims.sub,
      email: claims.email,
      displayName: claims.email?.split("@")[0] ?? "User",
    },
    isAdmin: claims.role === "admin",
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<Session | null>(null);
  const [ready, setReady] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [backend, setBackend] = React.useState<AuthBackend | null>(null);
  // Stable ref so ``getIdToken`` never changes identity — otherwise
  // useApiClient / usePaneTiles re-create on every auth change and loop.
  const backendRef = React.useRef<AuthBackend | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const built = await buildAuthBackend(await fetchAuthConfig());
        const token = await built.restore();
        if (cancelled) return;
        backendRef.current = built;
        setBackend(built);
        setSession(token ? sessionFromToken(token) : null);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Sign-in is unavailable.");
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getIdToken = React.useCallback(async () => {
    const current = backendRef.current;
    if (!current) return null;
    const token = await current.token();
    if (!token) setSession(null);
    return token;
  }, []);

  const value: AuthState = React.useMemo(() => {
    const start = (token: string) => {
      const next = sessionFromToken(token);
      if (!next) throw new Error("The server returned an unusable session token.");
      setSession(next);
    };
    const need = (): AuthBackend => {
      if (!backend) throw new Error(error ?? "Sign-in is still loading.");
      return backend;
    };
    return {
      user: session?.user ?? null,
      ready,
      isAdmin: ready ? (session?.isAdmin ?? false) : null,
      error,
      canSignUp: Boolean(backend?.signUp),
      signIn: async (email, password) => start(await need().signIn(email, password)),
      signUp: backend?.signUp
        ? async (email, password, name) => start(await backend.signUp!(email, password, name))
        : undefined,
      signOut: async () => {
        await backendRef.current?.signOut();
        setSession(null);
      },
      getIdToken,
    };
  }, [session, ready, error, backend, getIdToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
